import React, { useEffect, useState } from "react";
import { Card, CardBody } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import DropdownWithHeader from "../../DropdownWithHeader";
import { TotalProfitTitle } from "../../../Constants";
import { monthlyDropdownList } from "../../../../Data/Dashboard/Default";
import { H6, LI, P, UL } from "../../../../AbstractElements";
import { TotalInvestmentProps } from "../../../../Types/Dashboard.type";
import axios from "axios";
import { baseUrl } from "../../../FileUpload";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
export default function TotalInvestment({
  bodyClass,
  colClass,
  investmentList,
  height,
}: TotalInvestmentProps) {
  const token = localStorage.getItem("token");
  const [duration, setDuration] = useState("Weekly"); // default value
  const [loading, setLoading] = useState(true);
  const [investmentData, setInvestmentData] = useState({
    total_customer_amount: 0,
    total_agent_amount: 0,
    total_expense_amount: 0,
    profit: 0,
    currency: 0
  });

  const getQueryParam = (value: string) => {
    switch (value.toLowerCase()) {
      case "monthly":
        return "monthly";
      case "yearly":
        return "yearly";
      case "weekly":
      default:
        return "weekly";
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.post(
          `${baseUrl}call_history/calculation`,
          { duration: getQueryParam(duration) },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setInvestmentData(response.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, [duration]);
  

  const total = investmentData.total_customer_amount || 1;

  const dynamicChartOptions: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        borderRadius: 10,
        columnWidth: "50%",
        distributed: true, // This allows different colors for each bar
        dataLabels: {
          position: "top", // top, center, bottom
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return investmentData.currency ? `${investmentData.currency}${Number(val).toFixed(2)}` : `${val}`;
      },
      offsetY: -20,
      style: {
        fontSize: "12px",
        colors: ["#304758"],
      },
    },
    legend: {
      show: false,
    },
    xaxis: {
      categories: [
        ["Customer", "Total"],
        ["Profit"],
        ["Agent", "Total"],
        ["Expenses"],
      ],
      labels: {
        show: true,
        rotate: 0,
        hideOverlappingLabels: false,
        trim: false,
        style: {
          colors: ["#28a745", "#ffc107", "#dc3545", "#fd7e14"],
          fontSize: "10px",
          fontWeight: 600,
        },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      show: false, // Hide Y axis to make it cleaner like the reference
    },
    grid: {
      show: false, // Clean look
    },
    colors: ["#28a745", "#ffc107", "#dc3545", "#fd7e14"],
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function (val) {
          return investmentData.currency ? `${investmentData.currency}${Number(val).toFixed(2)}` : `${val}`;
        }
      }
    }
  };

  const dynamicChartSeries = [
    {
      name: "Amount",
      data: [
        investmentData.total_customer_amount,
        investmentData.profit,
        investmentData.total_agent_amount,
        investmentData.total_expense_amount,
      ],
    },
  ];

  const investmentListDynamic = [
    {
      id: 1,
      text: "Customer Total",
      amount: investmentData.total_customer_amount,
      currency: investmentData.currency,
    },
    {
      id: 2,
      text: "Profit", // Profit
      amount: investmentData.profit,
      currency: investmentData.currency,
    },
    {
      id: 3,
      text: "Agent Total",
      amount: investmentData.total_agent_amount,
      currency: investmentData.currency,
    },
    {
      id: 4,
      text: "Expenses", // Other Expenses
      amount: investmentData.total_expense_amount,
      currency: investmentData.currency,
    },
  ];

  const hasData =
    Number(investmentData.total_customer_amount) !== 0 ||
    Number(investmentData.total_agent_amount) !== 0 ||
    Number(investmentData.total_expense_amount) !== 0;

  return (
    <div className={colClass}>
      <Card>
        <DropdownWithHeader
          end
          headerClass="pb-0 card-no-border"
          heading={TotalProfitTitle}
          dropDownList={monthlyDropdownList}
          dropDownIcon
          dropDownClass="icon-dropdown"
          onChange={(selected: string) => setDuration(selected)}
        />
        <CardBody className={`investment-card ${bodyClass || ""}`}>
          {loading ? (
            <>
              <div id="investment" className="d-flex justify-content-around align-items-end" style={{ height: '210px', paddingBottom: '10px' }}>
                <Skeleton height={160} width={40} /> 
                <Skeleton height={100} width={40} />
                <Skeleton height={80} width={40} />
                <Skeleton height={60} width={40} />
              </div>
              <div className="d-flex align-center justify-content-center simple-list flex-row m-3">
                {Array.from({ length: 2 }).map((_, index) => (
                  <div className="text-center px-2" key={index}>
                    <Skeleton width={60} height={20} className="mb-1" />
                    <Skeleton width={40} height={18} />
                  </div>
                ))}
              </div>
               <div className="d-flex align-center justify-content-center simple-list flex-row m-3">
                {Array.from({ length: 2 }).map((_, index) => (
                  <div className="text-center px-2" key={index}>
                    <Skeleton width={60} height={20} className="mb-1" />
                    <Skeleton width={40} height={18} />
                  </div>
                ))}
              </div>
            </>
          ) : !hasData ? (
             <div
               className="d-flex align-items-center justify-content-center text-center"
               style={{ height: "300px" }}
             >
               <div>
                 <H6 className="mb-2 text-muted">No Data Available</H6>
                 <P className="text-muted">
                   No activity found for the selected duration.
                 </P>
               </div>
             </div>
           ) : (
            <>
              <div id="investment" style={{marginRight:"-20px"}}>
                <ReactApexChart
                  type="bar" // Changed from radialBar
                  options={dynamicChartOptions}
                  series={dynamicChartSeries}
                  height={280} // Increased height for better visibility
                 
                />
              </div>
             
            </>
          )}
           {investmentList && (
                <div className="">
                  <div className="row g-3">
                    {investmentListDynamic.map((item, index) => {
                      const colors = ["#28a745", "#ffc107", "#dc3545", "#fd7e14"];
                      return (
                        <div className="col-6 text-center" key={item.id}>
                           <P className="mb-0" style={{ color: colors[index], fontSize: '13px', fontWeight: '500' }}>
                            {item.text}
                          </P>
                          <H6>{item.currency || '$'}{Number(item.amount || 0).toFixed(2)}</H6>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
        </CardBody>
      </Card>
    </div>
  );
}




////pie chart
// import React, { useEffect, useState } from "react";
// import { Card, CardBody } from "reactstrap";
// import ReactApexChart from "react-apexcharts";
// import { ApexOptions } from "apexcharts";
// import DropdownWithHeader from "../../DropdownWithHeader";
// import { TotalProfitTitle } from "../../../Constants";
// import { monthlyDropdownList } from "../../../../Data/Dashboard/Default";
// import { H6, LI, P, UL } from "../../../../AbstractElements";
// import { TotalInvestmentProps } from "../../../../Types/Dashboard.type";
// import axios from "axios";
// import { baseUrl } from "../../../FileUpload";
// import Skeleton from "react-loading-skeleton";
// import "react-loading-skeleton/dist/skeleton.css";
// export default function TotalInvestment({
//   bodyClass,
//   colClass,
//   investmentList,
//   height,
// }: TotalInvestmentProps) {
//   const token = localStorage.getItem("token");
//   const [duration, setDuration] = useState("Weekly"); // default value
//   const [loading, setLoading] = useState(true);
//   const [investmentData, setInvestmentData] = useState({
//     total_customer_amount: 0,
//     total_agent_amount: 0,
//     remaining_amount: 0,
//     currency:0
//   });

//   const getQueryParam = (value: string) => {
//     switch (value.toLowerCase()) {
//       case "monthly":
//         return "monthly";
//       case "yearly":
//         return "yearly";
//       case "weekly":
//       default:
//         return "weekly";
//     }
//   };

//   useEffect(() => {
//     const fetchData = async () => {
//       setLoading(true);
//       try {
//         const response = await axios.post(
//           `${baseUrl}call_history/calculation`,
//           { duration: getQueryParam(duration) },
//           {
//             headers: {
//               Authorization: `Bearer ${token}`,
//             },
//           }
//         );
//         setInvestmentData(response.data);
//         // console.log(response.data);
//       } catch (error) {
//         console.error("Error fetching data:", error);
//       } finally {
//         setLoading(false);
//       }
//     };
  
//     fetchData();
//   }, [duration]);
  

//   const total = investmentData.total_customer_amount || 1;

//   const customerPercent = 90;
//   const agentPercent =
//     (customerPercent / total) * investmentData.total_agent_amount;
//   const remainingPercent =
//     (customerPercent / total) * investmentData.remaining_amount;

//   const dynamicChartOptions: ApexOptions = {
//     chart: {
//       type: "radialBar",
//     },
//     labels: ["Customer %", "Balance %", "Agent %"],
//     plotOptions: {
//       radialBar: {
//         dataLabels: {
//           name: { fontSize: "16px" },
//           value: {
//             fontSize: "14px",
//             formatter: (val) => `${Number(val).toFixed(1)}%`,
//           },
//         },
//       },
//     },
//     colors: ["#28a745", "#ffc107", "#dc3545"],
//   };

//   const dynamicChartSeries = [customerPercent, remainingPercent, agentPercent];

//   const investmentListDynamic = [
//     {
//       id: 1,
//       text: "Customer Total",
//       amount: investmentData.total_customer_amount,
//       currency: investmentData.currency,
//     },
//     {
//       id: 2,
//       text: "Agent Total",
//       amount: investmentData.total_agent_amount,
//       currency: investmentData.currency,
//     },
//     {
//       id: 3,
//       text: "Revenue Generated",
//       amount: investmentData.remaining_amount,
//       currency: investmentData.currency,
//     },
//   ];

//   return (
//     <div className={colClass}>
//       <Card>
//         <DropdownWithHeader
//           end
//           headerClass="pb-0 card-no-border"
//           heading={TotalProfitTitle}
//           dropDownList={monthlyDropdownList}
//           dropDownIcon
//           dropDownClass="icon-dropdown"
//           onChange={(selected: string) => setDuration(selected)}
//         />
//         <CardBody className={`investment-card ${bodyClass || ""}`}>
//           {loading ? (
//             <>
//               <div id="investment">
//                 <Skeleton circle height={260} width={260}/>
//               </div>
//               <UL className="d-flex align-center justify-content-center simple-list flex-row m-3">
//                 {Array.from({ length: 3 }).map((_, index) => (
//                   <LI className="text-center" key={index}>
//                     <Skeleton width={60} height={20} className="mb-1" />
//                     <Skeleton width={40} height={18} />
//                   </LI>
//                 ))}
//               </UL>
//             </>
//           ) : (
//             <>
//               <div id="investment">
//                 <ReactApexChart
//                   type="radialBar"
//                   options={dynamicChartOptions}
//                   series={dynamicChartSeries}
//                   height={height}
//                 />
//               </div>
//               {investmentList && (
//                 <UL className="d-flex align-center justify-content-center simple-list flex-row">
//                   {investmentListDynamic.map((item, index) => {
//                     const colors = ["#28a745", "#dc3545", "#ffc107"];
//                     return (
//                       <LI className="text-center" key={item.id}>
//                         <P className="mb-0" style={{ color: colors[index] }}>
//                           {item.text}
//                         </P>
//                         <H6>{item.currency}{Number(item.amount).toFixed(2)}</H6>
//                       </LI>
//                     );
//                   })}
//                 </UL>
//               )}
//             </>
//           )}
//         </CardBody>
//       </Card>
//     </div>
//   );
// }

// import React from 'react'
// import { Card, CardBody } from 'reactstrap'
// import ReactApexChart from 'react-apexcharts'
// import DropdownWithHeader from '../../DropdownWithHeader'
// import { TotalInvestmentTitle } from '../../../Constants'
// import { investmentListData, monthlyDropdownList } from '../../../../Data/Dashboard/Default'
// import { H6, LI, P, UL } from '../../../../AbstractElements'
// import { TotalInvestmentProps } from '../../../../Types/Dashboard.type'

// export default function TotalInvestment({ bodyClass, colClass, investmentList, chartData, height }: TotalInvestmentProps) {
//     return (
//         <div className={colClass}>
//             <Card>
//                 <DropdownWithHeader end headerClass='pb-0 card-no-border' heading={TotalInvestmentTitle} dropDownList={monthlyDropdownList} dropDownIcon dropDownClass='icon-dropdown' />
//                 <CardBody className={`investment-card ${bodyClass ? bodyClass : ''}`}>
//                     <div id="investment">
//                         <ReactApexChart type="radialBar" options={chartData} series={chartData.series} height={height} />
//                     </div>
//                     {investmentList &&
//                         <UL className="d-flex align-center justify-content-center simple-list flex-row">
//                             {investmentListData.map((item) => (
//                                 <LI className="text-center" key={item.id}>
//                                     <P className="mb-0">{item.text}</P>
//                                     <H6>{'$'}{item.amount}</H6>
//                                 </LI>
//                             ))}
//                         </UL>
//                     }
//                 </CardBody>
//             </Card>
//         </div>
//     )
// }
